/**
 * CommunityCertificatesPage — §8 Certificates.
 * Lists the volunteer's own recognition certificates (admin-issued only —
 * honours "only generate certificates based on actual verified participation";
 * this page is display-only, never self-issues). Each card shows the certificate
 * number, award title, programme tag, issue date, issued-by name and status
 * (ACTIVE / REVOKED). A "Verify" control runs the public PII-safe verification
 * lookup so the volunteer can confirm what an external verifier would see.
 * No financial concepts.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ScrollText, BadgeCheck, X, ShieldCheck, AlertTriangle } from 'lucide-react'
import { CommunityLayout, GovPageTitle, GovCard, GovEmptyState, GovButton } from '@/components/community/CommunityLayout'
import { SectionLoading } from '@/components/community/ui'
import { myCertificates, verifyCertificate, type Certificate, type CertificateVerify } from '@/services/certificateService'
import { formatCertificateDate } from '@/lib/utils'

export default function CommunityCertificatesPage() {
  const { t } = useTranslation()
  const [certs, setCerts] = useState<Certificate[]>([])
  const [loading, setLoading] = useState(true)
  const [verifyFor, setVerifyFor] = useState<Certificate | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true)
    myCertificates()
      .then((c) => { if (active) setCerts(c) })
      .catch(() => { if (active) setCerts([]) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [])

  return (
    <CommunityLayout>
      <GovPageTitle title={t('community.certificates.title')} subtitle={t('community.certificates.subtitle')} icon={ScrollText} />

      <GovCard className="mb-6">
        <p className="text-sm text-gray-600 flex items-start gap-2">
          <BadgeCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" aria-hidden="true" />
          {t('community.certificates.note')}
        </p>
      </GovCard>

      {loading ? <SectionLoading rows={2} /> : certs.length === 0 ? (
        <GovEmptyState icon="📜" title={t('community.certificates.none')} description={t('community.certificates.noneHint')} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3">
          {certs.map((c) => (
            <GovCard key={c.id}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shrink-0">
                  <ScrollText className="w-6 h-6 text-white" aria-hidden="true" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{c.awardTitle}</h3>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${c.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                      {t(`community.certificates.status_${c.status}`, c.status)}
                    </span>
                  </div>
                  {c.programmeTag && <p className="text-xs text-gray-500 mt-0.5">{c.programmeTag}</p>}
                  {c.description && <p className="text-sm text-gray-600 mt-1 line-clamp-2">{c.description}</p>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-500 flex-wrap">
                    <span className="font-mono">{c.certificateNumber}</span>
                    {c.issueDate && <span>· {formatCertificateDate(c.issueDate)}</span>}
                    {c.issuedByName && <span>· {t('community.certificates.issuedBy')} {c.issuedByName}</span>}
                  </div>
                  <button onClick={() => setVerifyFor(c)}
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:underline min-h-[40px]">
                    <ShieldCheck className="w-4 h-4" aria-hidden="true" />{t('community.certificates.verify')}
                  </button>
                </div>
              </div>
            </GovCard>
          ))}
        </div>
      )}

      {verifyFor && <VerifyModal cert={verifyFor} onClose={() => setVerifyFor(null)} />}
    </CommunityLayout>
  )
}

function VerifyModal({ cert, onClose }: { cert: Certificate; onClose: () => void }) {
  const { t } = useTranslation()
  const [result, setResult] = useState<CertificateVerify | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    setLoading(true); setError(null)
    verifyCertificate(cert.certificateNumber)
      .then((v) => { if (active) setResult(v) })
      .catch((e: any) => { if (active) setError(e?.response?.data?.message || t('errors.generic')) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [cert, t])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={t('community.certificates.verify')}>
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <GovCard className="relative w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{t('community.certificates.verify')}</h2>
          <button onClick={onClose} aria-label={t('community.actions.cancel')} className="p-2 rounded-lg hover:bg-gray-100 min-h-[40px] min-w-[40px] flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        {loading ? (
          <p className="text-sm text-gray-500 py-6 text-center">{t('common.saving')}</p>
        ) : error ? (
          <p className="text-sm text-red-600 py-4 text-center">{error}</p>
        ) : result && (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 rounded-lg px-3 py-2.5 ${result.valid ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {result.valid ? <BadgeCheck className="w-5 h-5" aria-hidden="true" /> : <AlertTriangle className="w-5 h-5" aria-hidden="true" />}
              <span className="text-sm font-semibold">{result.valid ? t('community.certificates.valid') : t('community.certificates.invalid')}</span>
            </div>
            <p className="text-sm text-gray-700"><span className="font-medium">{t('community.certificates.holder')}:</span> {result.volunteerName}</p>
            <p className="text-sm text-gray-700"><span className="font-medium">{t('community.certificates.award')}:</span> {result.awardTitle}</p>
            {result.programmeTag && <p className="text-sm text-gray-700"><span className="font-medium">{t('community.certificates.programme')}:</span> {result.programmeTag}</p>}
            {result.issueDate && <p className="text-sm text-gray-700"><span className="font-medium">{t('community.certificates.issuedOn')}:</span> {formatCertificateDate(result.issueDate)}</p>}
            {result.issuedByName && <p className="text-sm text-gray-700"><span className="font-medium">{t('community.certificates.issuedBy')}:</span> {result.issuedByName}</p>}
            <p className="text-xs text-gray-400 pt-1">{t('community.certificates.verifyNote')}</p>
          </div>
        )}
        <div className="flex justify-end pt-4">
          <GovButton variant="secondary" size="sm" onClick={onClose}>{t('common.close')}</GovButton>
        </div>
      </GovCard>
    </div>
  )
}